import { Integrations, RESTRICTION_ACCESS, SyncStatus } from 'src/constants/integrations';
import { TBooleanInNumber, TTranslate } from 'src/types/main';

export const posRestrictionKeysList = [
  'amountPercent',
  'vatIncludedToggle',
  'autoAcceptOnlinePaymentToggle',
  'defaultWorkingHours',
  'branchCreate',
  'branchAvailabilityChange',
  'branchFromPosDeleteNoFm',
  'branchAddress',
  'branchPhoneNumber',
  'branchOrderTypes',
  'branchDeliveryZones',
  'branchNames',
  'branchWorkingHours',
  'sectionsAndTables',
  'categoryCreate',
  'categoryDelete',
  'categoryRestore',
  'categoryName',
  'categoryDescription',
  'categoryAvailability',
  'categorySort',
  'itemCreate',
  'itemDelete',
  'itemRestore',
  'itemPortion',
  // Not exist for now
  'itemCalories',
  'itemOrderType',
  'itemName',
  'itemDescription',
  'itemCategories',
  'itemImages',
  'preSelectedOptions',
  'globalItemAvailability',
  'globalItemPrices',
  'globalItemStock',
  'branchItemAvailability',
  'branchItemStock',
  'branchItemPrice',
  'itemSizeToggle',
  'addModifierToItem',
  'deleteModifierFromItem',
  'addExclusionToItem',
  'donatableItemToggle',
  'onlyForComboToggle',
  'itemsSort',
  'itemModifierMin',
  'itemModifierMax',
  'uniqueOptionsAvailability',
  'singleChoice',
  'itemOptionGlobalPrice',
  'itemOptionBranchPrice',
  'itemOptionMax',
  'itemOptionGlobalAvailability',
  'itemOptionBranchAvailability',
  'modifierCreate',
  'modifierDelete',
  'modifierRestore',
  'modifierGlobalAvailability',
  'modifierName',
  'addOptionToModifier',
  'deleteOptionFromModifier',
  'restoreOptionInModifier',
  'optionName',
  'optionsPortion',
  'currency',
  // Not exist for now
  'optionsCalories',

  'optionGlobalPrice',
  'exclusionCreate',
  'exclusionRestore',
  // combo
  'comboMealCreate',
  'comboMealDelete',
  'comboMealRestore',
  'comboMealStatus',
  'comboMealImage',
  'comboMealName',
  'comboMealDescription',
  'comboCategories',
  'comboMealPrices',
  'comboMealChangeDiscountPrice',
  'comboMealItemModifier',
  // meal size
  'comboMealSizeCreate',
  'comboMealSizeDelete',
  'comboMealEditSizeName',
  // meal group
  'comboMealGroupCreate',
  'comboMealGroupDelete',
  'comboMealEditItemGroupName',
  // meal choice
  'comboMealItemChoiceCreate',
  'comboMealItemChoiceDelete',
  'comboMealEditItemChoiceName',
  'comboMealChangeItemInItemChoice',
] as const;

export type TPosRestrictionSettings = Record<(typeof posRestrictionKeysList)[number], RESTRICTION_ACCESS>;

export interface IPosIntRestriction {
  access: RESTRICTION_ACCESS | undefined;
  shouldDisable: boolean;
}

export interface IResponseIntegrations {
  adjust: boolean;
  applePay: boolean;
  checkout: boolean;
  connect: boolean;
  dook: boolean;
  foodics: boolean;
  googleMaps: boolean;
  googleTagManager: boolean;
  kudu: boolean;
  marn: boolean;
  t360: TBooleanInNumber;
  takerGo: boolean;
  yallow: boolean;
}

export type TIntegrationsAvailability = Record<Integrations, boolean>;
export const integrationCast = (data: IResponseIntegrations): TIntegrationsAvailability => {
  return {
    [Integrations.ADJUST]: Boolean(data.adjust),
    [Integrations.APPLE_PAY]: Boolean(data.applePay),
    [Integrations.CHECKOUT]: Boolean(data.checkout),
    [Integrations.CONNECT]: Boolean(data.connect),
    [Integrations.DOOK]: Boolean(data.dook),
    [Integrations.FOODICS]: Boolean(data.foodics),
    [Integrations.GOOGLE_MAPS]: Boolean(data.googleMaps),
    [Integrations.GOOGLE_TAG_MANAGER]: Boolean(data.googleTagManager),
    [Integrations.KUDU]: Boolean(data.kudu),
    [Integrations.MARN]: Boolean(data.marn),
    [Integrations.T360]: Boolean(data.t360),
    [Integrations.TAKER_GO]: Boolean(data.takerGo),
    [Integrations.YALLOW]: Boolean(data.yallow),
  };
};

export interface IIntegrationSettings<T> {
  enabled: TBooleanInNumber;
  settings: T;
}

export interface IMenuSet {
  id: number;
  category: string;
}

export interface IT360IntegrationsSettings<T> {
  enabled: TBooleanInNumber;
  integrations: T[];
  menu_sets: IMenuSet[];
}

export interface IT360IntegrationsErrors {
  [key: number]: { [key: string]: any };
}

export interface IFoodicsMenuGroup {
  id: string;
  name: string;
  deleted_at: Date | null;
}

export interface IFoodicsSettingsBranch {
  id: string;
  name: TTranslate;
  is_selected: boolean;
}

export interface IFoodicsSettings {
  business_name: string;
  business_reference: string;
  essenceAdvancedIntegrationType: {
    [key: string]: {
      [key: string]: TBooleanInNumber;
    };
  };
  f5_menu_group: string | null;
  groups: IFoodicsMenuGroup[];
  f5_sync_auto: TBooleanInNumber;
  sync_status: SyncStatus;
  f5_last_sync_date: Date;
  authorize_route: string;
  f5_delivery_autocomplete: TBooleanInNumber;

  branches: IFoodicsSettingsBranch[];
}

export interface IT360IntegrationSettings {
  id: number;
  name: string;
  provider: string;
  enabled: TBooleanInNumber;
  is_empty_tokens: TBooleanInNumber;
  settings: {
    [key: string]: any;
  };
  last_sync_date: Date;
  last_sync_error: any;
  is_active_sync: TBooleanInNumber;
  last_sync_successful: TBooleanInNumber;
}

export interface IT360IntegrationTab {
  id: number;
  name: string;
  provider: string;
}

// For ToYou Integration
export interface ITakerBranch {
  id: number;
  name: string;
  address: string | null;
  external_id: number | null;
}

export interface IToYouBranch {
  id: string; // UUID as a string
  name: string;
  formated_address: string | null;
  taker_branch_id: number | null;
  is_new_select?: boolean;
}

export interface ITakerAndToYouBranches {
  taker_branches: ITakerBranch[];
  to_you_branches: IToYouBranch[];
}

export interface TakerToYouBranchesMapping {
  [taker_branch_id: number]: string; // UUID as a string
}
